import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import {IssueState, type ParentIssue} from '../../api/common-contracts'
import type {GroupingMetadataWithSource} from '../../features/grouping/types'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {IssueModel} from '../../models/memex-item-model'
import {Omnibar, type OmnibarRef} from '../omnibar/omnibar'
import hierarchyDepthStyles from '../table/renderers/title-renderer.module.css'
import {CELL_HEIGHT} from './constants'
import styles from './sub-issue-omnibar-row.module.css'
import type {TableDataType} from './table-data-type'

type SubIssueOmnibarRowProps = {
  previousItemId: number
  parentRow: Row<TableDataType>
  onClose: () => void
  subIssueDepth?: number
}

/**
 * Wrapper component for rendering an Omnibar inline below a table row.
 * Handles closing the omnibar on blur or escape key.
 */
export function SubIssueOmnibarRow({previousItemId, parentRow, onClose, subIssueDepth = 0}: SubIssueOmnibarRowProps) {
  const omnibarRef = useRef<OmnibarRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const omnibarDepth = subIssueDepth + 3

  // Create an update column value action, which will automatically set the parent issue when creating a new issue or
  // adding an existing issue as a sub-issue.
  const parentIssueUpdateAction: UpdateColumnValueAction | undefined = useMemo(() => {
    const parentItem = parentRow.original
    if (!(parentItem instanceof IssueModel)) return undefined

    const nwo = parentItem.getNameWithOwner()
    if (!nwo || nwo.length !== 2) return undefined

    const [owner, repository] = nwo
    if (!owner || !repository) return undefined

    // Global Relay ID is required in order to create a sub-issue, because it uses the GraphQL API
    const globalRelayId = parentItem.content.globalRelayId
    if (!globalRelayId) return undefined

    // Construct a ParentIssue object from the parent row's data
    const parentIssue: ParentIssue = {
      id: parentItem.content.id,
      globalRelayId,
      number: parentItem.getItemNumber(),
      // This really can't ever be undefined, but our types don't accurately represent reality so we provide a fallback
      // just in case here.
      state: parentItem.state ?? IssueState.Open,
      stateReason: parentItem.stateReason,
      nwoReference: parentItem.getNameWithOwnerReference(),
      title: parentItem.getRawTitle(),
      titleHtml: parentItem.getHtmlTitle(),
      url: parentItem.content.url,
      repository,
      owner,
      updatedAt: parentItem.updatedAt || new Date().toISOString(),
    }

    return {
      dataType: MemexColumnDataType.ParentIssue,
      value: parentIssue,
    }
  }, [parentRow])

  const updateColumnActions: Array<UpdateColumnValueAction> = useMemo(() => {
    const actions = []

    if (parentIssueUpdateAction) {
      actions.push(parentIssueUpdateAction)
    }

    return actions
  }, [parentIssueUpdateAction])

  // Create grouping metadata on the client in order to properly apply the parent issue when adding a new sub-issue.
  // This will mimic the behavior of grouping by parent issue such that we can warn users if they're changing the
  // parent issue of an existing sub-issue.
  const groupingMetadata: GroupingMetadataWithSource | undefined = useMemo(() => {
    const parentIssue = parentIssueUpdateAction?.value
    if (!parentIssue) return undefined

    return {
      value: parentIssue.nwoReference,
      sourceObject: {
        kind: 'group' as const,
        dataType: MemexColumnDataType.ParentIssue,
        value: parentIssue,
      },
    } satisfies GroupingMetadataWithSource
  }, [parentIssueUpdateAction?.value])

  useEffect(() => {
    // Focus the omnibar when it opens
    requestAnimationFrame(() => {
      // TODO: Use table navigation reducer instead of manually focusing here, in order to support keyboard navigation.
      omnibarRef.current?.focus()
    })
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Escape') {
      onClose()
    }
  }

  // Handle focus leaving the omnibar container
  const handleFocusOut = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Check if the new focus target is outside the container
      if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <div
      ref={containerRef}
      onBlur={handleFocusOut}
      style={{
        height: `${CELL_HEIGHT}px`,
        '--hierarchy-depth': omnibarDepth,
      }}
      className={clsx(hierarchyDepthStyles.hierarchyDepth, styles.InlineOmnibar)}
    >
      <Omnibar
        ref={omnibarRef}
        defaultPlaceholder="Add a sub-issue"
        newItemAttributes={{
          previousItemId,
          isCreatingSubIssue: true,
          updateColumnActions,
          // Set the group ID to the parent issue as if we were grouped by parent issue
          groupId: groupingMetadata?.value,
        }}
        groupingMetadata={groupingMetadata}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

try{ SubIssueOmnibarRow.displayName ||= 'SubIssueOmnibarRow' } catch {}